import type { ParseKeys } from "i18next";
import { getWorkspaceSettingValue, workspaceSettingDefaults } from "src/services/appSettingsService/appSettingsService";
import type { Settings } from "src/types/settings";

// Namespace-prefixed keys only. The plain, unprefixed variants resolve against whichever namespace a
// component happens to list first, so they would not survive being passed between components here.
type PagesKey = Extract<ParseKeys<["pages"]>, `pages:${string}`>;
type SidepanelsKey = Extract<ParseKeys<["sidepanels"]>, `sidepanels:${string}`>;

export type WorkspaceSettingLabelKey = PagesKey | SidepanelsKey;

// Settings that are not a page's own navigation toggle: they switch one part of a page on or off, or
// they apply to the whole workspace. Each needs a label of its own, while every page toggle shares
// the same "show this page in the navigation" wording.
const workspaceOptionSettingNames = [
    "DownloadsPageShowsPublicProducts",
    "MembersRequireTwoFactorAuthentication",
    "OverviewPageShowsActivityChart",
] as const satisfies readonly API.WorkspaceSettingName[];

export type WorkspaceOptionSettingName = (typeof workspaceOptionSettingNames)[number];

export function isWorkspaceOptionSetting(name: string): name is WorkspaceOptionSettingName {
    return (workspaceOptionSettingNames as readonly string[]).includes(name);
}

type WorkspacePageFeature = {
    name: WorkspaceOptionSettingName;
    isAvailableForTenant?: (features: Settings["features"]) => boolean;
};

export type WorkspacePage = {
    name: API.WorkspaceSettingName;
    icon: string;
    tenantFlag: keyof Settings["features"]["pages"];
    features: WorkspacePageFeature[];
};

const workspacePages: WorkspacePage[] = [
    {
        name: "EnrollmentPageEnabled",
        icon: "fa-solid fa-plus",
        tenantFlag: "enrollmentPageEnabled",
        features: [],
    },
    {
        name: "OverviewPageEnabled",
        icon: "fa-solid fa-chart-line",
        tenantFlag: "overviewPageEnabled",
        features: [{ name: "OverviewPageShowsActivityChart", isAvailableForTenant: (features) => features.eventChartEnabled }],
    },
    {
        name: "DevicesPageEnabled",
        icon: "fa-solid fa-laptop",
        tenantFlag: "devicesPageEnabled",
        features: [],
    },
    {
        name: "GroupsPageEnabled",
        icon: "fa-solid fa-folder-tree",
        tenantFlag: "groupsPageEnabled",
        features: [],
    },
    {
        name: "ProvisioningPageEnabled",
        icon: "fa-solid fa-sliders",
        tenantFlag: "provisioningPageEnabled",
        features: [],
    },
    {
        name: "DownloadsPageEnabled",
        icon: "fa-regular fa-floppy-disk",
        tenantFlag: "downloadsPageEnabled",
        features: [{ name: "DownloadsPageShowsPublicProducts" }],
    },
    {
        name: "ProductsPageEnabled",
        icon: "fa-solid fa-microchip",
        tenantFlag: "productsPageEnabled",
        features: [],
    },
    {
        name: "FilesPageEnabled",
        icon: "fa-regular fa-file",
        tenantFlag: "filesPageEnabled",
        features: [],
    },
    // temporary: RolloutPageEnabled is hidden from workspace settings until the Rollouts page is ready for all users
    // issue: https://gitlab.com/emteria/development/web/frontend/hub/-/work_items/1103
];

// temporary: MembersRequireTwoFactorAuthentication is hidden because the backend does not enforce the
// requirement yet, so the switch would promise a protection the workspace does not get. Its label and
// confirmation dialog are kept, so re-enabling it means putting the name back into this array.
// issue: https://gitlab.com/emteria/development/web/frontend/hub/-/work_items/1199
export const workspaceGlobalSettingNames: WorkspaceOptionSettingName[] = [];

export function buildWorkspaceSettingsState(settingsList: readonly API.WorkspaceSetting[]) {
    const valueByName = {} as Record<API.WorkspaceSettingName, 0 | 1>;
    for (const name of Object.keys(workspaceSettingDefaults) as API.WorkspaceSettingName[]) {
        valueByName[name] = getWorkspaceSettingValue(settingsList, name) ? 1 : 0;
    }
    return { valueByName };
}

// The one place a setting is named, read by the configuration cards and by the workspace event renderer
// alike. A page toggle borrows the sidebar link's own name, so the page is called the same thing wherever
// it is mentioned; the remaining settings switch one part of a page on or off and carry a label of their
// own under "optionLabels".
//
// Deliberately "optionLabels" and not the "settings" these labels used to live under: that key still holds
// a {title, description} object in the five Weblate-maintained locales, and i18next renders an error for a
// key whose translated value is an object instead of falling back to English. A new key name falls back
// cleanly until Weblate has caught up, and leaves the old one to be dropped as an orphan.
// Spelled out rather than built from the setting name, so every key stays greppable in the locale files.
const workspaceSettingLabelKeys: Record<API.WorkspaceSettingName, WorkspaceSettingLabelKey> = {
    DevicesPageEnabled: "sidepanels:navigationMenu.devices",
    DownloadsPageEnabled: "sidepanels:navigationMenu.downloads",
    DownloadsPageShowsPublicProducts: "pages:workspacePage.configuration.optionLabels.DownloadsPageShowsPublicProducts",
    EnrollmentPageEnabled: "sidepanels:navigationMenu.deviceEnrollment",
    FilesPageEnabled: "sidepanels:navigationMenu.files",
    GroupsPageEnabled: "sidepanels:navigationMenu.groups",
    MembersRequireTwoFactorAuthentication: "pages:workspacePage.configuration.optionLabels.MembersRequireTwoFactorAuthentication",
    OverviewPageEnabled: "sidepanels:navigationMenu.dashboard",
    OverviewPageShowsActivityChart: "pages:workspacePage.configuration.optionLabels.OverviewPageShowsActivityChart",
    ProductsPageEnabled: "sidepanels:navigationMenu.products",
    ProvisioningPageEnabled: "sidepanels:navigationMenu.provisioning",
    RolloutPageEnabled: "sidepanels:navigationMenu.rollouts",
};

export function getWorkspaceSettingLabelKey(name: API.WorkspaceSettingName) {
    return workspaceSettingLabelKeys[name];
}

export function findWorkspaceSettingLabelKey(name: string): WorkspaceSettingLabelKey | undefined {
    return Object.hasOwn(workspaceSettingLabelKeys, name)
        ? workspaceSettingLabelKeys[name as API.WorkspaceSettingName]
        : undefined;
}

export function getVisibleWorkspacePages(features: Settings["features"]) {
    return workspacePages
        .filter((page) => features.pages[page.tenantFlag])
        .map((page) => ({
            ...page,
            features: page.features.filter((feature) => feature.isAvailableForTenant?.(features) ?? true),
        }));
}
